import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Loader2, UserPlus, ArrowLeft, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import { applyAuthToken } from '@/api/axiosSetup';
import InkPhDLogo from '@/components/InkPhDLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ThesisRegister — Sprint 7 hotfix.
 *
 * Creates a new InkPhD author account. Backend enforces:
 *   - password ≥ 12 chars, must include uppercase, lowercase, digit, special
 *   - username 3-50 chars, unique
 *   - email must be unique
 * After successful registration, auto-signs the user in via /api/auth/login
 * and redirects to /thesis (projects dashboard).
 */
export default function ThesisRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', username: '',
    town: '', country: '', occupation: '', researcher_role: 'PhD candidate',
    password: '', confirm: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const strong = form.password.length >= 12
    && /[A-Z]/.test(form.password) && /[a-z]/.test(form.password)
    && /\d/.test(form.password) && /[^A-Za-z0-9]/.test(form.password);
  const match = form.password && form.password === form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { setError('Password must be at least 12 characters and include uppercase, lowercase, digit, and a special character.'); return; }
    if (!match)  { setError('Passwords do not match'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register`, {
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
        town: form.town.trim() || 'N/A',
        country: form.country.trim() || 'N/A',
        occupation: form.occupation.trim() || 'Researcher',
        researcher_role: form.researcher_role.trim() || 'PhD candidate',
        first_name: form.first_name.trim() || null,
        last_name: form.last_name.trim() || null,
      });
      // Auto sign-in
      const loginRes = await axios.post(`${API}/auth/login`, {
        email: form.email.trim(), password: form.password,
      });
      if (loginRes.data?.user) {
        authStorage.setAuthorAuth(loginRes.data.user, loginRes.data.access_token);
        applyAuthToken(loginRes.data.access_token);
        toast.success(`Welcome to InkPhD, ${loginRes.data.user.username || loginRes.data.user.email}!`);
        navigate('/thesis/welcome', { replace: true });
      } else {
        toast.success('Account created — please sign in.');
        navigate('/thesis/login', { replace: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Registration failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-stone-50 via-background to-background dark:from-slate-950">
      <header className="border-b border-border bg-card/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="register-logo-home">
            <InkPhDLogo size="sm" />
          </Link>
          <Link to="/thesis/login" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-lg" data-testid="register-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-serif text-2xl" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
              <UserPlus className="h-5 w-5 text-[hsl(212,90%,55%)]" />
              Create your InkPhD account
            </CardTitle>
            <CardDescription>
              Start writing evidence-grounded scientific manuscripts with Vancouver-numbered citations, EQUATOR compliance, and defense-readiness scoring.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-3 text-sm text-red-700 dark:text-red-300 flex items-start gap-2 mb-4"
                data-testid="register-error">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fn">First name</Label>
                  <Input id="fn" value={form.first_name} onChange={set('first_name')} data-testid="register-firstname" />
                </div>
                <div>
                  <Label htmlFor="ln">Last name</Label>
                  <Input id="ln" value={form.last_name} onChange={set('last_name')} data-testid="register-lastname" />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" required autoComplete="email"
                  value={form.email} onChange={set('email')} data-testid="register-email" />
              </div>
              <div>
                <Label htmlFor="username">Username * <span className="text-[10px] text-muted-foreground">(3–50 chars)</span></Label>
                <Input id="username" required minLength={3} maxLength={50}
                  value={form.username} onChange={set('username')} data-testid="register-username" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="town">City / Town</Label>
                  <Input id="town" value={form.town} onChange={set('town')} data-testid="register-town" />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input id="country" value={form.country} onChange={set('country')} data-testid="register-country" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="occ">Occupation</Label>
                  <Input id="occ" placeholder="e.g. Physician, Researcher"
                    value={form.occupation} onChange={set('occupation')} data-testid="register-occupation" />
                </div>
                <div>
                  <Label htmlFor="role">Researcher role</Label>
                  <select id="role" value={form.researcher_role} onChange={set('researcher_role')}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="register-role">
                    <option>PhD candidate</option>
                    <option>Postdoctoral researcher</option>
                    <option>Clinical investigator</option>
                    <option>Faculty</option>
                    <option>Systematic reviewer</option>
                    <option>Medical writer</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="pw">Password * <span className="text-[10px] text-muted-foreground">(min 12 chars, mixed case, digit, special)</span></Label>
                <Input id="pw" type="password" required minLength={12}
                  value={form.password} onChange={set('password')} data-testid="register-password" />
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px]">
                  <span className={form.password.length >= 12 ? 'text-emerald-600' : 'text-slate-400'}>
                    {form.password.length >= 12 ? '✓' : '○'} 12+ chars
                  </span>
                  <span className={/[A-Z]/.test(form.password) ? 'text-emerald-600' : 'text-slate-400'}>
                    {/[A-Z]/.test(form.password) ? '✓' : '○'} Uppercase
                  </span>
                  <span className={/[a-z]/.test(form.password) ? 'text-emerald-600' : 'text-slate-400'}>
                    {/[a-z]/.test(form.password) ? '✓' : '○'} Lowercase
                  </span>
                  <span className={/\d/.test(form.password) ? 'text-emerald-600' : 'text-slate-400'}>
                    {/\d/.test(form.password) ? '✓' : '○'} Digit
                  </span>
                  <span className={/[^A-Za-z0-9]/.test(form.password) ? 'text-emerald-600' : 'text-slate-400'}>
                    {/[^A-Za-z0-9]/.test(form.password) ? '✓' : '○'} Special
                  </span>
                </div>
              </div>
              <div>
                <Label htmlFor="cf">Confirm password *</Label>
                <Input id="cf" type="password" required minLength={12}
                  value={form.confirm} onChange={set('confirm')} data-testid="register-confirm" />
                {form.confirm && (
                  <div className="mt-1 text-[10px]">
                    <span className={match ? 'text-emerald-600' : 'text-red-600'}>
                      {match ? '✓ Passwords match' : '✕ Passwords do not match'}
                    </span>
                  </div>
                )}
              </div>
              <Button
                type="submit" disabled={submitting || !strong || !match}
                className="w-full bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white disabled:opacity-50 mt-2"
                data-testid="register-submit-btn">
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account…</>
                  : <><CheckCircle2 className="w-4 h-4 mr-2" /> Create account</>}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center pt-2 border-t border-border">
                Already have an account?{' '}
                <Link to="/thesis/login" className="text-[hsl(212,90%,38%)] hover:underline font-semibold">
                  Sign in
                </Link>
              </p>
              <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                By creating an account you agree to our{' '}
                <Link to="/legal/terms" className="underline hover:text-foreground">Terms</Link> and{' '}
                <Link to="/legal/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
              </p>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
